import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Phone, AlertCircle, ArrowRight } from "lucide-react";
import GoogleLoginButton from "../../components/GoogleLoginButton";
import logo from "../../1122.png";

const apiUrl = process.env.REACT_APP_API_URL;

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('sessionExpired') === 'true') {
      setError("Your session has expired. Please log in again.");
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResendStatus("");
    setResendMessage("");

    // If identifier looks like a phone number, validate its format
    const looksLikePhone = /^[0-9+]/.test(identifier.trim());
    const phoneRegex = /^(\+251|251|0)[1-9][0-9]{8}$/;
    if (looksLikePhone && !phoneRegex.test(identifier.replace(/\s/g, ""))) {
      setError("Please enter a valid phone number (e.g. 0912345678 or +251912345678)");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password, rememberMe }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Success
      if (rememberMe) {
        localStorage.setItem("token", data.jwt);
        localStorage.setItem("user", JSON.stringify(data.user));
      } else {
        sessionStorage.setItem("token", data.jwt);
        sessionStorage.setItem("user", JSON.stringify(data.user));
      }
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!identifier) {
      setError("Please enter your email address first.");
      return;
    }
    setResendStatus("loading");
    setResendMessage("");
    try {
      const res = await fetch(`${apiUrl}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: identifier }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to resend verification email.");
      }
      setResendStatus("success");
      setResendMessage(data.message || "Verification email resent!");
    } catch (err) {
      setResendStatus("error");
      setResendMessage(err.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white my-auto justify-content-center">
      <div className="w-full flex justify-center z-50">
        <img
          src={logo}
          alt="Login background"
          className="w-20 h-20 object-contain"
        />
      </div>
      <div className="w-1/2 mx-auto flex items-center justify-center px-6 py-12 border rounded-lg shadow-lg my-10">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-gray-600">
              New here?{" "}
              <Link
                to="/register"
                className="font-semibold text-green-600 hover:text-green-500"
              >
                Create an account
              </Link>
            </p>
          </div>

          <div className="space-y-6">
            {/* Google Auth */}
            <div>
              <div className="w-full flex justify-center lg:justify-start">
                <GoogleLoginButton />
              </div>

              <div className="relative mt-8">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">
                    Or continue with
                  </span>
                </div>
              </div>
            </div>

            {/* Manual Auth Form */}
            <form onSubmit={handleLogin} className="space-y-6">
               {error && (
                <div className="space-y-2">
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4" /> {error}
                  </div>
                  {error.toLowerCase().includes("verify your email") && resendStatus !== "success" && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendStatus === "loading"}
                      className="text-sm font-semibold text-green-600 hover:text-green-500 disabled:opacity-50"
                    >
                      {resendStatus === "loading" ? "Resending..." : "Didn't receive the email? Resend verification link"}
                    </button>
                  )}
                </div>
              )}

              {resendStatus === "success" && (
                <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
                  {resendMessage}
                </div>
              )}

              {resendStatus === "error" && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {resendMessage}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email or Phone Number
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {identifier.match(/^[0-9+]/) ? (
                      <Phone className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Mail className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <input
                    type="text"
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="block w-full pl-10 px-3 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 sm:text-sm outline-none transition"
                    placeholder="you@example.com or +251 912 345 678"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 px-3 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 sm:text-sm outline-none transition"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-green-600 hover:text-green-500"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign in"}
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
